import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppInsightsContext,
  MetadataContext,
  ModalPortal,
  ProgressCircular,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import { useTranslations } from 'next-intl';
import { audioFileExistOnCloud, getAudioFileFromCloud } from '@/api';
import classNames from 'classnames';
import Play from '@/assets/play.svg';
import { useRouter } from 'next/router';
import XLight from '@/assets/xLight.svg';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';

export type AudioPlayerType = {
  chipClassName: string;
};

const AudioPlayer = ({ chipClassName }: AudioPlayerType) => {
  const t = useTranslations();
  const { pageId, pageTitle } = useContext(MetadataContext);
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const router = useRouter();

  const [reportHasAudio, setReportHasAudio] = useState<boolean>(false);
  const [loadingAudio, setLoadingAudio] = useState<boolean>(false);
  const [playerOpen, setPlayerOpen] = useState<boolean>(false);
  const [audioFileURL, setAudioFileURL] = useState<string | null>(null);
  const [playbackRate, setPlaybackRate] = useState<boolean>(false);
  const [audioFileBlob, setAudioFileBlob] = useState<Blob | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    audioFileExistOnCloud(pageId).then((result) => {
      setReportHasAudio(result);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.playbackRate = playbackRate ? 1.5 : 1;
    }
  }, [playbackRate]);

  const isiOS = useMemo(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAppleDevice = navigator.userAgent.includes('Macintosh');
    const isTouchScreen = navigator.maxTouchPoints >= 1; // true for iOS 13 (and hopefully beyond)

    return isIOS || (isAppleDevice && isTouchScreen);
  }, []);

  if (!reportHasAudio || !pageId) {
    return null;
  }

  const playAudio = () => {
    if (loadingAudio) {
      return;
    }

    if (audioFileURL) {
      setPlayerOpen(true);
      return;
    }

    setLoadingAudio(true);

    appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.AUDIO_PLAY_BACK, TrackEventType.DOWNLOAD, user, {
      pageId,
      pagePath: router.asPath,
    });

    getAudioFileFromCloud(pageId)
      .then((audioFile) => {
        const binaryString = window.atob(audioFile.audio.fileContents);
        const binaryLen = binaryString.length;
        const byteArray = new Uint8Array(binaryLen);
        for (let i = 0; i < binaryLen; i++) {
          const ascii = binaryString.charCodeAt(i);
          byteArray[i] = ascii;
        }

        const blob = new Blob([byteArray], {
          type: 'audio/mp3',
        });

        setAudioFileBlob(blob);

        setAudioFileURL(URL.createObjectURL(blob));
        setLoadingAudio(false);
        setPlayerOpen(true);
      })
      .finally(() => {
        setLoadingAudio(false);
      });
  };

  const downloadAudioData = () => {
    if (audioFileBlob) {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(audioFileBlob);
      link.download = pageTitle + '.mp3';
      link.click();
    }
  };

  return (
    <>
      <button className={classNames(chipClassName, 'cursor-pointer')} onClick={playAudio}>
        {loadingAudio ? (
          <ProgressCircular indeterminate size={24} width={3} className="mx-4" colorClassName="stroke-black " />
        ) : (
          <Play className="fill-from-bg stroke-from-bg mx-4 h-6 w-6 shrink-0" />
        )}
        <div className="pr-4">{loadingAudio ? 'Loading audio' : t('general.$listen2')}</div>
      </button>

      {playerOpen && (
        <ModalPortal>
          <div className="fixed right-4 bottom-19 flex flex-col rounded-xl bg-white/60 p-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <label className="m-5 inline-flex cursor-pointer items-center">
                  <input
                    id="playbackRate"
                    type="checkbox"
                    checked={playbackRate}
                    onChange={() => setPlaybackRate(!playbackRate)}
                    className="peer sr-only"
                  />
                  <div
                    className={classNames(
                      'bg-neutral-quaternary peer relative h-3.5 w-9 rounded-full bg-black/40',
                      "after:absolute after:-start-[2px] after:-top-[3px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-xl/30 after:transition-all after:content-['']",
                      'peer-focus:outline-none',
                      'peer-checked:after:border-buffer peer-checked:bg-brand peer-checked:bg-black/60 peer-checked:after:translate-x-full peer-checked:after:bg-black',
                    )}
                  ></div>
                  <span className="ms-3 text-base font-bold select-none">
                    {playbackRate ? 'Faster Speed' : 'Normal Speed'}
                  </span>
                </label>
                {isiOS && (
                  <button className="btn" onClick={downloadAudioData}>
                    <DownloadSimpleLight className="h-6 w-6" />
                  </button>
                )}
              </div>

              <button className="btn !px-2" onClick={() => setPlayerOpen(false)}>
                <XLight className="h-6 w-6" />
              </button>
            </div>
            <audio
              ref={audioPlayerRef}
              id="audio"
              controls
              className="audioPlayback"
              controlsList="noplaybackrate"
              autoPlay
              src={audioFileURL || ''}
            >
              <p>Your browser does not support audio.</p>
            </audio>
          </div>
        </ModalPortal>
      )}
    </>
  );
};

export default AudioPlayer;
